import { useState } from "react";
import api from "../services/api";
import Swal from "sweetalert2";

export default function useCart() {
  const [cart, setCart] = useState([]);

  /**
   * Agregar producto CON presentación específica
   */
  const addProductWithPresentation = (product, presentation) => {
    const precio = Number(presentation.precio_venta);
    if (isNaN(precio)) return;

    setCart(prev => {
      const uniqueKey = `${product.id}-${presentation.id}`;
      const found = prev.find(p => p.uniqueKey === uniqueKey);

      if (found) {
        const nuevaCantidad = found.cantidad + 1;
        
        // 🆕 Validar stock disponible para variantes
        if (presentation.tipo_presentacion === 'variante') {
          const stockDisponible = presentation.stock_actual || presentation.stock_independiente || 0;
          if (nuevaCantidad > stockDisponible) {
            Swal.fire({
              icon: 'warning',
              title: 'Stock insuficiente',
              text: `Solo hay ${stockDisponible} unidades disponibles de "${presentation.descripcion}"`,
              confirmButtonText: 'Entendido'
            });
            return prev; // No actualizar el carrito
          }
        }
        
        return prev.map(p =>
          p.uniqueKey === uniqueKey
            ? {
                ...p,
                cantidad: nuevaCantidad,
                subtotal: nuevaCantidad * p.precio_unitario
              }
            : p
        );
      }

      const newItem = {
        uniqueKey,
        product_id: product.id,
        presentation_id: presentation.id,
        codigo: product.codigo || `PROD-${product.id}`,
        nombre: `${product.nombre} - ${presentation.descripcion}`,
        precio_unitario: precio,
        cantidad: 1,
        subtotal: precio,
        gravado: Boolean(product.aplica_iva),
        factor_conversion: presentation.factor_conversion,
        tipo_presentacion: presentation.tipo_presentacion || 'empaque', // 🆕 NUEVO
        stock_disponible: presentation.stock_actual || null // 🆕 NUEVO
      };

      return [...prev, newItem];
    });
  };

  /**
   * Maneja productos con y sin presentaciones
   */
  const addProductByCode = async (rawQuery) => {
    try {
      if (!rawQuery) return;
      const query = rawQuery.trim();
      let productData = null;

      // Intentar buscar por código
      try {
        const resCode = await api.get(`/products/code/${query}`);
        productData = resCode.data;
      } catch (err) {
        // Si no encuentra, buscar por nombre
        const resAll = await api.get("/products");
        const allProducts = resAll.data.data ?? resAll.data;
        const matches = allProducts.filter(p =>
          p.nombre.toLowerCase().includes(query.toLowerCase())
        );

        if (matches.length === 0) throw new Error("Producto no encontrado");
        
        if (matches.length === 1) {
          const singleProduct = matches[0];
          const resCode = await api.get(`/products/code/${singleProduct.codigo}`);
          productData = resCode.data;
        } else {
          const selected = await Swal.fire({
            title: "Seleccione producto",
            input: "select",
            inputOptions: matches.reduce((acc, p) => {
              acc[p.id] = `${p.codigo || 'SIN-COD'} - ${p.nombre}`;
              return acc;
            }, {}),
            inputPlaceholder: "Selecciona un producto",
            showCancelButton: true
          });

          if (!selected.isConfirmed) return;
          
          const selectedProduct = matches.find(p => p.id === Number(selected.value));
          const resCode = await api.get(`/products/code/${selectedProduct.codigo}`);
          productData = resCode.data;
        }
      }

      // CASO 1: Producto CON presentaciones
      if (productData.has_presentations) {
        const { product, presentations } = productData;

        // 🆕 ACTUALIZADO: Mostrar stock para variantes
        const presentationOptions = presentations.reduce((acc, pres) => {
          let label = `${pres.descripcion} - $${pres.precio_venta.toFixed(2)}`;
          
          // Agregar información de stock si es variante
          if (pres.tipo_presentacion === 'variante') {
            const stock = pres.stock_actual || pres.stock_independiente || 0;
            label += ` (Stock: ${stock})`;
          }
          
          acc[pres.id] = label;
          return acc;
        }, {});

        const result = await Swal.fire({
          title: `📦 ${product.nombre}`,
          html: `
            <div style="margin-bottom: 8px; color: #718096; font-size: 14px;">
              Usa las flechas ↑↓ y presiona Enter
            </div>
          `,
          input: "select",
          inputOptions: presentationOptions,
          inputValue: presentations[0].id,
          showCancelButton: true,
          confirmButtonText: "✓ Agregar",
          cancelButtonText: "✕ Cancelar",
          didOpen: () => {
            const select = Swal.getInput();
            if (select) {
              setTimeout(() => select.focus(), 100);
            }
          },
          customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-secondary",
          },
        });

        if (!result.isConfirmed) return;

        const selectedPresentation = presentations.find(
          p => p.id === Number(result.value)
        );

        // 🆕 Validar stock antes de agregar (para variantes)
        if (selectedPresentation.tipo_presentacion === 'variante') {
          const stockDisponible = selectedPresentation.stock_actual || selectedPresentation.stock_independiente || 0;
          if (stockDisponible <= 0) {
            Swal.fire({
              icon: 'error',
              title: 'Sin stock',
              text: `No hay unidades disponibles de "${selectedPresentation.descripcion}"`,
              confirmButtonText: 'Entendido'
            });
            return;
          }
        }

        addProductWithPresentation(product, selectedPresentation);
        return;
      }

      // CASO 2: Producto SIN presentaciones
      const precio = Number(productData.precio_venta || productData.precio);
      if (isNaN(precio)) throw new Error("Precio inválido");

      setCart(prev => {
        const found = prev.find(p => p.product_id === productData.id && !p.presentation_id);
        
        if (found) {
          const nuevaCantidad = found.cantidad + 1;
          return prev.map(p =>
            p.product_id === productData.id && !p.presentation_id
              ? { ...p, cantidad: nuevaCantidad, subtotal: nuevaCantidad * p.precio_unitario }
              : p
          );
        }

        return [
          ...prev,
          {
            uniqueKey: `${productData.id}-simple`,
            product_id: productData.id,
            presentation_id: null,
            codigo: productData.codigo_barra || productData.codigo || `PROD-${productData.id}`,
            nombre: productData.nombre,
            precio_unitario: precio,
            cantidad: 1,
            subtotal: precio,
            gravado: Boolean(productData.aplica_iva),
            tipo_presentacion: null,
            stock_disponible: null
          }
        ];
      });

    } catch (error) {
      console.error(error);
      Swal.fire("Error", error.message || "Producto no encontrado", "error");
    }
  };

  /**
   * Para agregar desde resultados de búsqueda
   */
  const addProduct = async (product) => {
    try {
      const resCode = await api.get(`/products/code/${product.codigo}`);
      const productData = resCode.data;

      // Si tiene presentaciones, usar la lógica de selección
      if (productData.has_presentations) {
        const { product: prod, presentations } = productData;

        // 🆕 ACTUALIZADO: Mostrar stock para variantes
        const presentationOptions = presentations.reduce((acc, pres) => {
          let label = `${pres.descripcion} - $${pres.precio_venta.toFixed(2)}`;
          
          // Agregar información de stock si es variante
          if (pres.tipo_presentacion === 'variante') {
            const stock = pres.stock_actual || pres.stock_independiente || 0;
            label += ` (Stock: ${stock})`;
          }
          
          acc[pres.id] = label;
          return acc;
        }, {});

        const result = await Swal.fire({
          title: `📦 ${prod.nombre}`,
          html: `
            <div style="margin-bottom: 8px; color: #718096; font-size: 14px;">
              Usa las flechas ↑↓ y presiona Enter
            </div>
          `,
          input: "select",
          inputOptions: presentationOptions,
          inputValue: presentations[0].id,
          showCancelButton: true,
          confirmButtonText: "✓ Agregar",
          cancelButtonText: "✕ Cancelar",
          didOpen: () => {
            const select = Swal.getInput();
            if (select) {
              setTimeout(() => select.focus(), 100);
            }
          },
          customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-secondary",
          },
        });

        if (!result.isConfirmed) return;

        const selectedPresentation = presentations.find(
          p => p.id === Number(result.value)
        );

        // 🆕 Validar stock antes de agregar (para variantes)
        if (selectedPresentation.tipo_presentacion === 'variante') {
          const stockDisponible = selectedPresentation.stock_actual || selectedPresentation.stock_independiente || 0;
          if (stockDisponible <= 0) {
            Swal.fire({
              icon: 'error',
              title: 'Sin stock',
              text: `No hay unidades disponibles de "${selectedPresentation.descripcion}"`,
              confirmButtonText: 'Entendido'
            });
            return;
          }
        }

        addProductWithPresentation(prod, selectedPresentation);
        return;
      }

      // Sin presentaciones, agregar normalmente
      const precio = Number(product.precio_venta || product.precio);
      if (isNaN(precio)) return;

      setCart(prev => {
        const found = prev.find(p => p.product_id === product.id && !p.presentation_id);

        if (found) {
          const nuevaCantidad = found.cantidad + 1;
          return prev.map(p =>
            p.product_id === product.id && !p.presentation_id
              ? {
                  ...p,
                  cantidad: nuevaCantidad,
                  subtotal: nuevaCantidad * p.precio_unitario
                }
              : p
          );
        }

        return [
          ...prev,
          {
            uniqueKey: `${product.id}-simple`,
            product_id: product.id,
            presentation_id: null,
            codigo: product.codigo || `PROD-${product.id}`,
            nombre: product.nombre,
            precio_unitario: precio,
            cantidad: 1,
            subtotal: precio,
            gravado: Boolean(product.aplica_iva),
            tipo_presentacion: null,
            stock_disponible: null
          }
        ];
      });

    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo agregar el producto", "error");
    }
  };
/**
 * Agregar producto manual (sin inventario)
 */
const addManualItem = (descripcion, precio, cantidad = 1) => {
  const precioNum = Number(precio);
  const cantidadNum = Number(cantidad);
  if (!descripcion || isNaN(precioNum) || precioNum <= 0) return;

  const uniqueKey = `manual-${Date.now()}`;

  setCart(prev => [
    ...prev,
    {
      uniqueKey,
      product_id: null,
      presentation_id: null,
      es_manual: true,
      codigo: "MANUAL",
      nombre: descripcion,
      precio_unitario: precioNum,
      cantidad: cantidadNum,
      subtotal: precioNum * cantidadNum,
      gravado: true,
      tipo_presentacion: null,
      stock_disponible: null
    }
  ]);
};
  const updateQuantity = (uniqueKey, cantidad) => {
    setCart(prev =>
      prev.map(p => {
        if (p.uniqueKey !== uniqueKey) return p;
        const qty = cantidad === "" ? "" : Number(cantidad);
        
        // 🆕 Validar stock disponible para variantes al cambiar cantidad
        if (p.tipo_presentacion === 'variante' && p.stock_disponible !== null && qty > p.stock_disponible) {
          Swal.fire({
            icon: 'warning',
            title: 'Stock insuficiente',
            text: `Solo hay ${p.stock_disponible} unidades disponibles`,
            confirmButtonText: 'Entendido'
          });
          return p; // No actualizar
        }
        
        return {
          ...p,
          cantidad: qty,
          subtotal: qty === "" ? 0 : qty * p.precio_unitario
        };
      })
    );
  };

  const removeItem = (uniqueKey) => {
    setCart(prev => prev.filter(p => p.uniqueKey !== uniqueKey));
  };

  const subtotal = cart.reduce((sum, p) => sum + p.subtotal, 0);
  const totalGravado = cart.filter(p => p.gravado).reduce((sum, p) => sum + p.subtotal, 0);
  const totalExento = cart.filter(p => !p.gravado).reduce((sum, p) => sum + p.subtotal, 0);

  const buildPayload = (clientId) => ({
    client_id: clientId,
    fecha_venta: new Date().toISOString().slice(0, 10),
    condicion_operacion: "CONTADO",
    details: cart.map(p => ({
      product_id: p.product_id,
      presentation_id: p.presentation_id || null,
      cantidad: p.cantidad,
      precio_unitario: p.precio_unitario
    }))
  });

  const clearCart = () => setCart([]);

  return {
    cart,
    subtotal,
    totalGravado,
    totalExento,
    addProduct,
    addProductByCode,
    addProductWithPresentation,
    addManualItem,
    updateQuantity,
    removeItem,
    buildPayload,
    clearCart
  };
}